import { useEffect, useState } from 'react';
import { Alert, Avatar, Box, Button, Stack, Typography } from '@mui/material';
import { http } from '../../core/http/client';
import { HubSection, errorMessage } from './HubUi';
import type { OrganizationProfile, OrganizationProfilePhotos, ProfilePhoto } from './hubApi';

/** What PlatformAPI returns from POST .../photos/{kind}/upload-session (a one-time Media Server link). */
interface UploadSession {
  uploadUrl: string;
  uploadToken: string;
  method?: string;
  maxBytes: number;
  accept?: string[];
}

const ACCEPT = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];
const TYPE_MESSAGE = 'Choose a JPEG, PNG, WebP, GIF or AVIF photo.';

/** Media Server problem codes (README "Browser" API) in words a business owner can act on. */
const UPLOAD_ERRORS: Record<string, string> = {
  file_too_large: 'This photo is too large. Choose a smaller one.',
  unsupported_media_type: TYPE_MESSAGE,
  invalid_image: 'This photo could not be read. Choose another file.',
  image_too_large: 'This photo has too many pixels. Choose a smaller one.',
  malware_detected: 'This file was blocked by the virus scanner.',
  upload_expired: 'The upload took too long. Please try again.',
  upload_already_used: 'The upload link was already used. Please try again.',
  scanner_unavailable: 'Photos cannot be checked right now. Please try again later.',
};

async function uploadFailure(response: Response): Promise<Error> {
  if (response.status === 429) return new Error('Too many uploads. Please wait a minute and try again.');
  const problem = await response.json().catch(() => null) as { code?: string } | null;
  return new Error((problem?.code && UPLOAD_ERRORS[problem.code]) || 'The photo could not be uploaded. Please try again.');
}

export const PHOTO_TYPES = ACCEPT;

/**
 * Uploads one photo to the Media Server and returns its media id (still pending: the caller's
 * PlatformAPI endpoint commits it). `sessionPath` is the PlatformAPI route that checks the
 * caller's permission and hands out a one-time upload link.
 */
export async function uploadPhoto(sessionPath: string, file: File): Promise<string> {
  if (!ACCEPT.includes(file.type) || file.size === 0) throw new Error(TYPE_MESSAGE);
  // 1. PlatformAPI checks the permission and asks the Media Server for a one-time link.
  const { data: session } = await http().post<UploadSession>(sessionPath);
  if (session.accept && !session.accept.includes(file.type)) throw new Error(TYPE_MESSAGE);
  if (file.size > session.maxBytes)
    throw new Error(`Photos must be at most ${Math.floor(session.maxBytes / (1024 * 1024))} MB.`);
  // 2. The bytes go straight to the Media Server (raw body, no PlatformAPI credentials).
  const response = await fetch(session.uploadUrl, {
    method: session.method ?? 'PUT',
    headers: { 'X-Upload-Token': session.uploadToken, 'Content-Type': file.type },
    body: file,
  });
  if (!response.ok) throw await uploadFailure(response);
  const media = await response.json() as { id: string };
  return media.id;
}

/** A photo PlatformAPI still serves from storage.Files: it needs the signed-in request. */
function useLegacySrc(photo: ProfilePhoto | null | undefined) {
  const url = photo && !photo.mediaId ? photo.cardUrl : null;
  const [src, setSrc] = useState<string>();
  useEffect(() => {
    setSrc(undefined);
    if (!url) return;
    const controller = new AbortController();
    let objectUrl: string | undefined;
    const path = url.replace(/^\/api\/v1\/platform/, '');
    void http().get<Blob>(path, { responseType: 'blob', signal: controller.signal, skipErrorToast: true })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(data);
        setSrc(objectUrl);
      }).catch(() => undefined);
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url]);
  return src;
}

/**
 * Media Server variants: the browser picks thumb/card/full from srcset for the rendered size, and
 * the dominant colour plus the 32 px placeholder show until it arrives (no layout shift: width/height).
 */
function MediaImage({ photo, alt, sizes, cover }: { photo: ProfilePhoto; alt: string; sizes: string; cover: boolean }) {
  // A plain <img>: MUI's Box would treat width/height as style props instead of the intrinsic size.
  return <img src={cover ? photo.fullUrl : photo.cardUrl} srcSet={photo.srcset ?? undefined} sizes={sizes}
    width={photo.width ?? undefined} height={photo.height ?? undefined} alt={alt} decoding="async"
    style={{
      width: '100%', height: '100%', objectFit: 'cover', display: 'block',
      backgroundColor: photo.dominantColor ?? undefined,
      backgroundImage: photo.lqipUrl ? `url(${photo.lqipUrl})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center',
    }} />;
}

function Photo({ photo, label, cover }: { photo: ProfilePhoto | null | undefined; label: string; cover: boolean }) {
  const legacySrc = useLegacySrc(photo);
  const media = photo?.mediaId ? photo : null;
  if (cover)
    return <Box sx={{ height: 180, width: '100%', bgcolor: 'action.hover', borderRadius: 2, overflow: 'hidden', display: 'grid', placeItems: 'center' }}>
      {media ? <MediaImage photo={media} alt={label} cover sizes="(max-width: 900px) 100vw, 900px" />
        : legacySrc ? <Box component="img" src={legacySrc} alt={label} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <Typography color="text.secondary">No cover photo</Typography>}
    </Box>;
  return <Avatar alt={label} src={media ? undefined : legacySrc} sx={{ width: 96, height: 96, bgcolor: media?.dominantColor ?? undefined }}>
    {media ? <MediaImage photo={media} alt={label} cover={false} sizes="96px" /> : label.slice(0, 1)}
  </Avatar>;
}

type Profile = OrganizationProfile & OrganizationProfilePhotos;

/** Falls back to the URL-only fields for a response that predates the photo objects. */
function photoOf(profile: Profile, kind: 'profile' | 'cover'): ProfilePhoto | null {
  const photo = kind === 'profile' ? profile.logo : profile.cover;
  if (photo !== undefined) return photo;
  const url = kind === 'profile' ? profile.logoUrl : profile.coverUrl;
  return url ? { mediaId: null, lqipUrl: null, thumbUrl: url, cardUrl: url, fullUrl: url, srcset: null, width: null, height: null, dominantColor: null } : null;
}

export function ProfilePhotos({ profile, canUpdate, onSaved }: { profile: Profile; canUpdate: boolean; onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const change = async (kind: 'profile' | 'cover', file?: File) => {
    setError(null);
    if (file && (!ACCEPT.includes(file.type) || file.size === 0)) { setError(TYPE_MESSAGE); return; }
    setBusy(true);
    try {
      const path = `/organization-profiles/${encodeURIComponent(profile.id)}/photos/${kind}`;
      if (!file) {
        await http().delete(path);
      } else {
        // PlatformAPI checks platform.company.update for the link, then commits the new media and releases the previous one.
        const mediaId = await uploadPhoto(`${path}/upload-session`, file);
        await http().put(path, { mediaId });
      }
      onSaved();
    } catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  };
  return <HubSection title="Profile and cover photos" subtitle="Add photos for this business. JPEG, PNG, WebP, GIF or AVIF, up to 15 MB each.">
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    <Stack spacing={3}>
      {(['profile', 'cover'] as const).map(kind => {
        const photo = photoOf(profile, kind);
        const label = kind === 'profile' ? 'Profile photo' : 'Cover photo';
        return <Stack key={kind} spacing={1.5}>
          <Typography fontWeight={600}>{label}</Typography>
          <Photo photo={photo} label={profile.displayName || profile.productName} cover={kind === 'cover'} />
          <Stack direction="row" spacing={1}>
            <Button component="label" variant="outlined" disabled={!canUpdate || busy}>
              {photo ? 'Change photo' : 'Upload photo'}
              <input hidden type="file" aria-label={`Upload ${label.toLowerCase()}`} accept={ACCEPT.join(',')} disabled={!canUpdate || busy}
                onChange={event => { const file = event.target.files?.[0]; event.target.value = ''; if (file) void change(kind, file); }} />
            </Button>
            {photo && <Button color="error" disabled={!canUpdate || busy} onClick={() => void change(kind)}>Remove photo</Button>}
          </Stack>
        </Stack>;
      })}
    </Stack>
  </HubSection>;
}
